import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  FolderOpen,
  Camera,
  Loader2,
  Info,
  Check,
  Pencil,
  Tag,
  Cpu,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  gradient,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Field,
  FieldGrid,
  FieldFull,
  Label,
  Input,
  Select,
  ErrorSpan,
  RequiredStar,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";
import { AlbumOption, FolderOption, EditSessionFormState, EditSessionFormErrors } from "./types";
import {
  updateC2CSession,
  C2CSession,
  UpdateC2CSessionPayload,
} from "../../../../Redux/ApiCalls/Dashboard/C2CAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import apiCall from "../../../../Redux/ApiCalls/AuthorizedApi";

interface EditSessionModalProps {
  session: C2CSession;
  onClose: () => void;
  onSuccess: (updatedSession: C2CSession) => void;
}

const ALBUMS_URL = `/project/user/projects/?page=1&page_size=100`;
const FOLDERS_URL = (albumId: number) =>
  `/project/user/get-folder/?project_id=${albumId}&fetch=4`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const FieldInput = styled(Input)<{ $hasError?: boolean }>`
  border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(23, 23, 23, 0.1)")};
  &:focus {
    border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(201, 77, 170, 0.4)")};
    box-shadow: ${(p) =>
      p.$hasError ? "0 0 0 3px rgba(228, 72, 98, 0.12)" : "0 0 0 3px rgba(201, 77, 170, 0.1)"};
  }
`;

const FieldSelect = styled(Select)<{ $hasError?: boolean }>`
  border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(23, 23, 23, 0.1)")};
  &:focus {
    border-color: ${(p) => (p.$hasError ? "#e44862" : "rgba(201, 77, 170, 0.4)")};
    box-shadow: ${(p) =>
      p.$hasError ? "0 0 0 3px rgba(228, 72, 98, 0.12)" : "0 0 0 3px rgba(201, 77, 170, 0.1)"};
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.08) 0%, rgba(206, 86, 176, 0.06) 100%);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.68);
  line-height: 1.55;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 24px 24px;
  flex-shrink: 0;
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 110px;
  height: 46px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  color: rgba(23, 23, 23, 0.72);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 160px;
  height: 46px;
  padding: 0 22px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.32);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

export const EditSessionModal: React.FC<EditSessionModalProps> = ({
  session,
  onClose,
  onSuccess,
}) => {
  const [albums, setAlbums] = useState<AlbumOption[]>([]);
  const [albumsLoading, setAlbumsLoading] = useState(false);
  const [folders, setFolders] = useState<FolderOption[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [form, setForm] = useState<EditSessionFormState>({
    project_id: session.album?.id ?? "",
    folder_id: session.folder?.id ?? "",
    device_name: session.device?.name ?? "",
    device_brand: session.device?.brand ?? "",
    device_model: (session.device as any)?.model ?? "",
  });
  const [formErrors, setFormErrors] = useState<EditSessionFormErrors>({});
  const [saving, setSaving] = useState(false);

  const loadFolders = async (albumId: number, preserveFolderId?: number) => {
    setFoldersLoading(true);
    setFolders([]);
    try {
      const res = await apiCall({ method: "GET", url: FOLDERS_URL(albumId) });
      const raw = res?.data ?? res;
      let list: FolderOption[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
        ? raw.results
        : Array.isArray(raw?.data)
        ? raw.data
        : [];

      // Ensure session's current folder is present if this is the original album
      if (
        albumId === session.album?.id &&
        session.folder &&
        !list.some((f) => f.id === session.folder.id)
      ) {
        list = [{ id: session.folder.id, name: session.folder.name }, ...list];
      }

      setFolders(list);

      if (preserveFolderId && list.some((f) => f.id === preserveFolderId)) {
        setForm((prev) => ({ ...prev, folder_id: preserveFolderId }));
      } else {
        setForm((prev) => ({ ...prev, folder_id: "" }));
      }
    } catch {
      showErrorToast("Could not load folders for the selected album");
    } finally {
      setFoldersLoading(false);
    }
  };

  const loadAlbums = async () => {
    setAlbumsLoading(true);
    try {
      const res = await apiCall({ method: "GET", url: ALBUMS_URL });
      const raw = res?.data ?? res;
      let list: AlbumOption[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
        ? raw.results
        : Array.isArray(raw?.data)
        ? raw.data
        : [];

      // Ensure session's current album is included
      if (session.album && !list.some((a) => a.id === session.album.id)) {
        list = [{ id: session.album.id, name: session.album.name }, ...list];
      }

      setAlbums(list);
    } catch {
      showErrorToast("Could not load albums");
    } finally {
      setAlbumsLoading(false);
    }
  };

  useEffect(() => {
    loadAlbums();
    if (session.album?.id) {
      loadFolders(session.album.id, session.folder?.id);
    }
  }, []);


  const validateForm = (): boolean => {
    const errors: EditSessionFormErrors = {};
    if (!form.project_id) errors.project_id = "Please select an album";
    if (!form.folder_id) errors.folder_id = "Please select a folder";
    if (!form.device_name.trim()) errors.device_name = "Device name is required";
    if (!form.device_brand.trim()) errors.device_brand = "Device brand is required";
    if (!form.device_model.trim()) errors.device_model = "Device model is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload: UpdateC2CSessionPayload = {
        project_id: Number(form.project_id),
        folder_id: Number(form.folder_id),
        device_name: form.device_name.trim(),
        device_brand: form.device_brand.trim(),
        device_model: form.device_model.trim(),
      };
      const res = await updateC2CSession(session.session_id, payload);

      const selectedAlbum = albums.find((a) => a.id === Number(form.project_id));
      const selectedFolder = folders.find((f) => f.id === Number(form.folder_id));

      const updatedSession: C2CSession = {
        ...session,
        ...(res?.data || {}),
        album:
          res?.data?.album?.name
            ? res.data.album
            : selectedAlbum
            ? { id: selectedAlbum.id, name: selectedAlbum.name }
            : session.album,
        folder:
          res?.data?.folder?.name
            ? res.data.folder
            : selectedFolder
            ? { id: selectedFolder.id, name: selectedFolder.name }
            : session.folder,
        device: {
          ...session.device,
          ...(res?.data?.device || {}),
          name: res?.data?.device?.name || form.device_name.trim(),
          brand: res?.data?.device?.brand || form.device_brand.trim(),
          model: (res?.data?.device as any)?.model || form.device_model.trim(),
        },
      };

      showSuccessToast("Session updated successfully");
      onSuccess(updatedSession);
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message ?? "";
      if (status === 404 && msg.toLowerCase().includes("album")) {
        setFormErrors((e) => ({ ...e, project_id: "Album not found or not accessible" }));
      } else if (status === 404 && msg.toLowerCase().includes("folder")) {
        setFormErrors((e) => ({ ...e, folder_id: "Folder not found" }));
      } else {
        showErrorToast(msg || "Could not update session. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay
        id="c2c-edit-modal-overlay"
        onClick={onClose}
      >
        <ModalShell
          style={{ maxWidth: 520 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Pencil size={11} />
                  Edit Session
                </HeroBadge>
                <ModalTitle>Edit Session</ModalTitle>
                <ModalSubtitle>
                  Update destination album, folder, or camera device details.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn
                id="c2c-edit-modal-close"
                type="button"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            <InfoBox>
              <Info size={16} style={{ flexShrink: 0, color: "#c94daa", marginTop: 1 }} />
              <span>
                Your camera remains connected via the same FTPS credentials. Future photo uploads
                will be routed into the selected album and folder. Existing photos remain untouched.
              </span>
            </InfoBox>

            <FieldFull as="div">
              <Label htmlFor="c2c-edit-album-select">
                <FormLabelIcon icon={FolderOpen} />
                Album
                <RequiredStar>*</RequiredStar>
              </Label>
              <FieldSelect
                id="c2c-edit-album-select"
                value={form.project_id}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : "";
                  setForm((f) => ({ ...f, project_id: id, folder_id: "" }));
                  setFormErrors((er) => ({ ...er, project_id: undefined, folder_id: undefined }));
                  if (id) {
                    const restoreFolder = id === session.album?.id ? session.folder?.id : undefined;
                    loadFolders(Number(id), restoreFolder);
                  } else {
                    setFolders([]);
                  }
                }}
                $hasError={!!formErrors.project_id}
                disabled={albumsLoading || saving}
              >
                <option value="">
                  {albumsLoading ? "Loading albums…" : "Select album"}
                </option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.id === session.album?.id ? "(Current)" : ""}
                  </option>
                ))}
              </FieldSelect>
              {formErrors.project_id && <ErrorSpan>{formErrors.project_id}</ErrorSpan>}
            </FieldFull>

            <FieldFull as="div">
              <Label htmlFor="c2c-edit-folder-select">
                <FormLabelIcon icon={FolderOpen} />
                Folder
                <RequiredStar>*</RequiredStar>
              </Label>
              <FieldSelect
                id="c2c-edit-folder-select"
                value={form.folder_id}
                onChange={(e) => {
                  const fid = e.target.value ? Number(e.target.value) : "";
                  setForm((f) => ({ ...f, folder_id: fid }));
                  setFormErrors((er) => ({ ...er, folder_id: undefined }));
                }}
                $hasError={!!formErrors.folder_id}
                disabled={!form.project_id || foldersLoading || saving}
              >
                <option value="">
                  {foldersLoading
                    ? "Loading folders…"
                    : !form.project_id
                    ? "Select an album first"
                    : folders.length === 0
                    ? "No folders found in this album"
                    : "Select folder"}
                </option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {f.id === session.folder?.id && Number(form.project_id) === session.album?.id
                      ? " (Current)"
                      : ""}
                  </option>
                ))}
              </FieldSelect>
              {formErrors.folder_id && <ErrorSpan>{formErrors.folder_id}</ErrorSpan>}
            </FieldFull>

            <FieldFull as="div">
              <Label htmlFor="c2c-edit-device-name">
                <FormLabelIcon icon={Camera} />
                Device Name
                <RequiredStar>*</RequiredStar>
              </Label>
              <FieldInput
                id="c2c-edit-device-name"
                placeholder="e.g. Canon R5"
                maxLength={100}
                value={form.device_name}
                onChange={(e) => {
                  setForm((f) => ({ ...f, device_name: e.target.value }));
                  setFormErrors((er) => ({ ...er, device_name: undefined }));
                }}
                $hasError={!!formErrors.device_name}
                disabled={saving}
              />
              {formErrors.device_name && <ErrorSpan>{formErrors.device_name}</ErrorSpan>}
            </FieldFull>

            <FieldGrid>
              <Field>
                <Label htmlFor="c2c-edit-device-brand">
                  <FormLabelIcon icon={Tag} />
                  Brand
                  <RequiredStar>*</RequiredStar>
                </Label>
                <FieldInput
                  id="c2c-edit-device-brand"
                  placeholder="e.g. Canon"
                  maxLength={100}
                  value={form.device_brand}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, device_brand: e.target.value }));
                    setFormErrors((er) => ({ ...er, device_brand: undefined }));
                  }}
                  $hasError={!!formErrors.device_brand}
                  disabled={saving}
                />
                {formErrors.device_brand && <ErrorSpan>{formErrors.device_brand}</ErrorSpan>}
              </Field>
              <Field>
                <Label htmlFor="c2c-edit-device-model">
                  <FormLabelIcon icon={Cpu} />
                  Model
                  <RequiredStar>*</RequiredStar>
                </Label>
                <FieldInput
                  id="c2c-edit-device-model"
                  placeholder="e.g. EOS R5"
                  maxLength={100}
                  value={form.device_model}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, device_model: e.target.value }));
                    setFormErrors((er) => ({ ...er, device_model: undefined }));
                  }}
                  $hasError={!!formErrors.device_model}
                  disabled={saving}
                />
                {formErrors.device_model && <ErrorSpan>{formErrors.device_model}</ErrorSpan>}
              </Field>
            </FieldGrid>
          </ModalBody>

          <ModalFooter>
            <SecondaryBtn
              id="c2c-edit-modal-cancel"
              type="button"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </SecondaryBtn>
            <PrimaryBtn
              id="c2c-edit-modal-submit"
              type="button"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? <Spinner size={16} /> : <Check size={16} />}
              {saving ? "Saving…" : "Save Changes"}
            </PrimaryBtn>
          </ModalFooter>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};
